// @ts-nocheck
import React, { useState, useMemo, useEffect } from 'react';
import {
    LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, ReferenceLine, Legend, ComposedChart
} from 'recharts';
import {
    Zap, Wind, AlertTriangle, Battery, ArrowRight, Calendar, MapPin, Activity,
    TrendingUp, TrendingDown, Clock, ChevronRight, ArrowLeft, Table as TableIcon,
    BarChart2, Download, Filter, Leaf, Target, Maximize2, Minimize2, Info, CheckCircle2, AlertCircle,
    Layers, PieChart, Users, Factory, IndianRupee, Calculator, Sliders, CheckCircle, Loader2
} from 'lucide-react';
import { useEnergyAnalysisStore } from '../store/EnergyStackStore';

// --- MOCK API SERVICE ---
// Simulating an async fetch from a local JSON file or API endpoint


const fetchEnergyData = () => {

    return new Promise(async (resolve) => {

        const consumerRawDataJson = await fetch("/consumption_daily_average_2025-08.json", {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        }).then(res => res.json());

        const generatorRawDataJson = await fetch("/generation_daily_average_2025-08.json", {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        }).then(res => res.json());

        resolve({ consumerRawData: consumerRawDataJson, generatorRawData: generatorRawDataJson });
    });

};

// Mock Hourly Market Clearing Price (IEX) in ₹/kWh
const hourlyMCP = [
    3.50, 3.20, 3.10, 3.05, 3.10, 3.40, 4.10, 5.50, 6.20, 5.80,
    4.50, 4.20, 4.10, 4.00, 4.15, 4.80, 6.50, 8.50, 10.20, 9.80,
    8.50, 6.50, 5.20, 4.10
];

// Utility to generate color shades
const generateColorPalette = (keys, baseHue, saturation = 70, lightnessStart = 35, lightnessEnd = 65) => {
    const step = (lightnessEnd - lightnessStart) / Math.max(keys.length - 1, 1);

    return keys.reduce((acc, key, index) => {
        const lightness = lightnessStart + step * index;
        acc[key] = `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
        return acc;
    }, {});
};

// --- Dynamic Insight Analysis Helper ---
const analyzeEnergyData = (data) => {
    // 1. Peak Demand Mismatch Analysis
    const deficitHours = data.filter(d => d.balance < 0);
    let mismatchText = "No critical deficit detected.";
    let mismatchTime = "N/A";
    let mismatchNote = "Generation covers consumption.";

    if (deficitHours.length > 0) {
        // Find the worst hour
        const worstHour = deficitHours.reduce((prev, curr) => prev.balance < curr.balance ? prev : curr);
        const worstHourIdx = parseInt(worstHour.hour.split(':')[0]);

        // Find contiguous block around worst hour
        let startIdx = worstHourIdx;
        while (startIdx > 0 && data[startIdx - 1].balance < 0) startIdx--;

        let endIdx = worstHourIdx;
        while (endIdx < 23 && data[endIdx + 1].balance < 0) endIdx++;

        mismatchTime = `${String(startIdx).padStart(2, '0')}:00 – ${String(endIdx + 1).padStart(2, '0')}:00`;

        // Find max consumption and min generation in this window
        const windowData = data.slice(startIdx, endIdx + 1);
        const maxConsInWindow = Math.max(...windowData.map(d => d.consumption));
        const minGenInWindow = Math.min(...windowData.map(d => d.generation));

        mismatchText = "Critical deficit window";
        mismatchNote = `Extreme deficit overlap. Generation drops to <${Math.ceil(minGenInWindow).toLocaleString()} kWh while consumption peaks >${Math.floor(maxConsInWindow).toLocaleString()} kWh. Immediate dispatch required.`;
    }

    // 2. Generation Profile Analysis
    const morningData = data.slice(0, 9); // 00-08
    const middayData = data.slice(9, 17); // 09-16
    const eveningData = data.slice(17, 24); // 17-23

    const avgGenMorning = morningData.reduce((acc, curr) => acc + curr.generation, 0) / morningData.length;
    const avgGenEvening = eveningData.reduce((acc, curr) => acc + curr.generation, 0) / eveningData.length;
    const minGenEvening = Math.min(...eveningData.map(d => d.generation));

    const genProfile = {
        morning: `High output (~${Math.round(avgGenMorning).toLocaleString()} kWh/hr). Ideal for heavy storage charging.`,
        midday: "Fluctuating generation. Check specific plant output for stability.",
        evening: `Generation averages ~${Math.round(avgGenEvening).toLocaleString()} kWh/hr, dropping to ${Math.round(minGenEvening).toLocaleString()} kWh at lowest point.`
    };

    // 3. BESS Strategy Analysis (Windows only)
    // Find longest surplus block (Charge)
    let maxSurplusRun = 0, currentSurplusRun = 0, surplusStart = 0, bestSurplusStart = 0;
    for (let i = 0; i < 24; i++) {
        if (data[i].balance > 0) {
            if (currentSurplusRun === 0) surplusStart = i;
            currentSurplusRun++;
        } else {
            if (currentSurplusRun > maxSurplusRun) {
                maxSurplusRun = currentSurplusRun;
                bestSurplusStart = surplusStart;
            }
            currentSurplusRun = 0;
        }
    }
    // Check end of array case
    if (currentSurplusRun > maxSurplusRun) {
        maxSurplusRun = currentSurplusRun;
        bestSurplusStart = surplusStart;
    }

    const chargeWindow = maxSurplusRun > 0
        ? `${String(bestSurplusStart).padStart(2, '0')}:00-${String(bestSurplusStart + maxSurplusRun).padStart(2, '0')}:00`
        : "N/A";

    // Find longest deficit block (Discharge)
    let maxDeficitRun = 0, currentDeficitRun = 0, deficitStart = 0, bestDeficitStart = 0;
    for (let i = 0; i < 24; i++) {
        if (data[i].balance < 0) {
            if (currentDeficitRun === 0) deficitStart = i;
            currentDeficitRun++;
        } else {
            if (currentDeficitRun > maxDeficitRun) {
                maxDeficitRun = currentDeficitRun;
                bestDeficitStart = deficitStart;
            }
            currentDeficitRun = 0;
        }
    }
    // Check end of array case
    if (currentDeficitRun > maxDeficitRun) {
        maxDeficitRun = currentDeficitRun;
        bestDeficitStart = deficitStart;
    }

    const dischargeWindow = maxDeficitRun > 0
        ? `${String(bestDeficitStart).padStart(2, '0')}:00-${String(bestDeficitStart + maxDeficitRun).padStart(2, '0')}:00`
        : "N/A";

    return { mismatchTime, mismatchText, mismatchNote, genProfile, chargeWindow, dischargeWindow };
};

const KPICard = ({ title, value, subtext, icon: Icon, trend, colorClass }) => (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-start justify-between hover:shadow-md transition-shadow">
        <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
            {subtext && <p className={`text-xs mt-2 font-medium ${trend === 'positive' ? 'text-emerald-600' : trend === 'negative' ? 'text-rose-600' : 'text-slate-400'}`}>
                {subtext}
            </p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClass} bg-opacity-10`}>
            <Icon className={`w-6 h-6 ${colorClass.replace('bg-', 'text-')}`} />
        </div>
    </div>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

        return (
            <div className="bg-white p-4 border border-slate-200 shadow-lg rounded-lg text-sm z-50 min-w-[200px]">
                <p className="font-bold text-slate-700 mb-2 border-b border-slate-100 pb-1">{label}</p>
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {sortedPayload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                <span className="text-slate-500 text-xs capitalize">{entry.name}:</span>
                            </div>
                            <span className="font-mono font-medium text-xs">{entry.value.toLocaleString()} {entry.name.includes('Cost') || entry.name.includes('Savings') ? '₹' : 'kWh'}</span>
                        </div>
                    ))}
                    {payload.some(p => p.name === 'Generation' || p.name === 'Total Generation') && (
                        <div className="mt-2 pt-2 border-t border-slate-100 flex justify-between">
                            <span className="text-slate-500 text-xs font-bold">Net Balance:</span>
                            <span className={`font-bold text-xs ${(payload[0].payload.generation - payload[0].payload.consumption) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {(payload[0].payload.generation - payload[0].payload.consumption).toFixed(2)} kWh
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    return null;
};

// --- Actual Green Energy Consumption Component ---
const ActualGreenConsumption = ({ data }) => {
    const [viewMode, setViewMode] = useState('graph');

    const processedData = useMemo(() => {
        return data.map(item => {
            const actualGreen = Math.min(item.generation, item.consumption);
            const isFullyGreen = item.generation >= item.consumption;
            return {
                ...item,
                actualGreen,
                isFullyGreen,
                greenStatus: isFullyGreen ? 'Fully Green' : 'Partial Green',
                greenPercentage: item.consumption > 0 ? ((actualGreen / item.consumption) * 100).toFixed(1) : 0
            };
        });
    }, [data]);

    const totalActualGreen = processedData.reduce((acc, curr) => acc + curr.actualGreen, 0);
    const totalConsumption = processedData.reduce((acc, curr) => acc + curr.consumption, 0);
    const greenPercentageTotal = totalConsumption > 0 ? ((totalActualGreen / totalConsumption) * 100).toFixed(1) : 0;

    const lowestGreen = [...processedData].sort((a, b) => a.actualGreen - b.actualGreen)[0] || processedData[0];
    const highestGreen = [...processedData].sort((a, b) => b.actualGreen - a.actualGreen)[0] || processedData[0];

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Leaf className="w-5 h-5 text-green-600" />
                        Actual Green Energy Consumption (Hourly)
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">Tracks actual renewable absorption against demand.</p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="group relative">
                        <Info className="w-5 h-5 text-slate-400 hover:text-slate-600 cursor-help" />
                        <div className="absolute right-0 top-8 w-64 bg-slate-800 text-white text-xs p-3 rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            Actual Green Consumption = min(Generation, Consumption) per hour. Surplus generation is not counted towards hourly consumption coverage.
                        </div>
                    </div>
                    <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${viewMode === 'table' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <TableIcon className="w-3.5 h-3.5" /> Table
                        </button>
                        <button
                            onClick={() => setViewMode('graph')}
                            className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${viewMode === 'graph' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <BarChart2 className="w-3.5 h-3.5" /> Graph
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {viewMode === 'table' && (
                    <div className="overflow-x-auto rounded-lg border border-slate-100">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-4 py-3 font-semibold">Hour</th>
                                    <th className="px-4 py-3 font-semibold text-right">Generation</th>
                                    <th className="px-4 py-3 font-semibold text-right">Consumption</th>
                                    <th className="px-4 py-3 font-semibold text-right text-green-700">Actual Green</th>
                                    <th className="px-4 py-3 font-semibold text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {processedData.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-4 py-2.5 font-medium text-slate-700">{row.hour}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-500">{row.generation.toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-500">{row.consumption.toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-right font-bold text-green-700">{row.actualGreen.toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-center">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${row.isFullyGreen
                                                ? 'bg-green-100 text-green-800 border border-green-200'
                                                : 'bg-amber-100 text-amber-800 border border-amber-200'
                                                }`}>
                                                {row.isFullyGreen ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                                {row.greenStatus}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {viewMode === 'graph' && (
                    <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={processedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={2} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                                <Tooltip cursor={{ fill: '#f0fdf4' }} content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div className="bg-white p-3 border border-slate-200 shadow-xl rounded-lg text-sm z-50">
                                                <p className="font-bold text-slate-800 mb-2">{data.hour}</p>
                                                <div className="space-y-1">
                                                    <p className="flex justify-between gap-4"><span className="text-slate-500">Total Cons:</span> <span className="font-mono">{data.consumption.toLocaleString()}</span></p>
                                                    <p className="flex justify-between gap-4"><span className="text-green-600 font-bold">Actual Green:</span> <span className="font-mono font-bold text-green-700">{data.actualGreen.toLocaleString()}</span></p>
                                                    <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between gap-4">
                                                        <span className="text-slate-500 text-xs uppercase">Green %</span>
                                                        <span className={`font-bold ${data.isFullyGreen ? 'text-green-600' : 'text-amber-600'}`}>{data.greenPercentage}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    return null;
                                }} />
                                <Legend />
                                <Area type="monotone" dataKey="consumption" fill="#f1f5f9" stroke="#cbd5e1" strokeDasharray="5 5" name="Total Demand" />
                                <Bar dataKey="actualGreen" name="Green Energy Consumed" radius={[4, 4, 0, 0]}>
                                    {processedData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.isFullyGreen ? '#16a34a' : '#fbbf24'} />
                                    ))}
                                </Bar>
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-slate-100">
                    <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Total Actual Green</p>
                        <p className="text-lg font-bold text-green-700">{totalActualGreen.toLocaleString()} <span className="text-xs font-normal text-slate-500">kWh</span></p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Green Percentage</p>
                        <div className="flex items-center gap-2">
                            <p className="text-lg font-bold text-slate-800">{greenPercentageTotal}%</p>
                            <Target className="w-4 h-4 text-green-500" />
                        </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Lowest Green Hr</p>
                        <div className="flex items-center gap-2">
                            <p className="text-lg font-bold text-slate-800">{lowestGreen.hour}</p>
                            <span className="text-xs text-rose-500 font-bold bg-rose-50 px-1.5 py-0.5 rounded">{lowestGreen.actualGreen.toLocaleString()} kWh</span>
                        </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Highest Green Hr</p>
                        <div className="flex items-center gap-2">
                            <p className="text-lg font-bold text-slate-800">{highestGreen.hour}</p>
                            <span className="text-xs text-green-600 font-bold bg-green-50 px-1.5 py-0.5 rounded">{highestGreen.actualGreen.toLocaleString()} kWh</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 text-xs text-slate-500 flex items-start gap-2">
                <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <span>
                    <strong>Regulatory Note:</strong> This view reflects real-time hourly green energy matching. Daily surplus generation does not guarantee 100% green consumption compliance.
                </span>
            </div>
        </div>
    );
};

// --- NEW COMPONENT: Cost Analysis View ---
const CostAnalysisView = ({ onBack, dashboardData, totalCons }) => {
    const [batteryCapacity, setBatteryCapacity] = useState(0); // kWh
    const PPA_RATE = 3.12; // ₹/kWh

    // Calculate Costs & Savings dynamically based on Battery Capacity
    const { costData, summary } = useMemo(() => {
        let currentStorage = 0;
        let totalGreenCost = 0;
        let totalDeficitCost = 0;
        let totalReferenceCost = 0;
        let totalGreenEnergyUsed = 0;

        // Simulation Data
        const simulatedData = dashboardData.map(d => {
            const surplus = Math.max(0, d.generation - d.consumption);
            const deficit = Math.max(0, d.consumption - d.generation);

            // Battery Logic: Charge on Surplus, Discharge on Deficit
            let batteryAction = 0; // +Charge, -Discharge
            let usedFromStorage = 0;

            if (surplus > 0) {
                const space = batteryCapacity - currentStorage;
                const charge = Math.min(surplus, space);
                currentStorage += charge;
                batteryAction = charge;
            } else if (deficit > 0) {
                const discharge = Math.min(deficit, currentStorage);
                currentStorage -= discharge;
                usedFromStorage = discharge;
                batteryAction = -discharge;
            }

            const remainingDeficit = Math.max(0, deficit - usedFromStorage);
            const actualGreenConsumed = Math.min(d.generation, d.consumption) + usedFromStorage;

            // Costs
            const greenCost = actualGreenConsumed * PPA_RATE;
            const deficitCost = remainingDeficit * d.mcp;
            const refCost = d.consumption * d.mcp; // If 100% bought from market

            totalGreenCost += greenCost;
            totalDeficitCost += deficitCost;
            totalReferenceCost += refCost;
            totalGreenEnergyUsed += actualGreenConsumed;

            return {
                hour: d.hour,
                greenCost,
                deficitCost,
                refCost,
                mcp: d.mcp,
                greenCoveragePct: d.consumption > 0 ? (actualGreenConsumed / d.consumption) * 100 : 0,
                isFullyGreen: actualGreenConsumed >= d.consumption - 0.1 // Tolerance
            };
        });

        const totalCost = totalGreenCost + totalDeficitCost;
        const netSavings = totalReferenceCost - totalCost;
        const re100Compliance = totalCons > 0 ? (totalGreenEnergyUsed / totalCons) * 100 : 0;

        return {
            costData: simulatedData,
            summary: { totalCost, totalReferenceCost, netSavings, re100Compliance }
        };
    }, [batteryCapacity, dashboardData, totalCons]);

    return (
        <div className="animate-in fade-in duration-300 pb-12">
            {/* 1️⃣ Header & Sticky Assumptions */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <nav className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={onBack}>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="font-semibold text-slate-900">Cost Analysis & RE100</span>
                    </nav>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">Hourly Cost Analysis</h1>
                                <p className="text-xs text-slate-500 font-medium mt-1">Granular RE100 Compliance & Financial Impact</p>
                            </div>
                        </div>

                        {/* Assumptions Pills */}
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2">
                                <Leaf className="w-3 h-3" /> Green PPA: ₹3.12/kWh
                            </div>
                            <div className="bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2">
                                <Activity className="w-3 h-3" /> Deficit: IEX Hourly MCP
                            </div>
                            <div className="bg-slate-100 text-slate-600 border border-slate-200 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2">
                                <Clock className="w-3 h-3" /> Resolution: Hourly
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

                {/* 3️⃣ Logic Card */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2 mb-2">
                            <Calculator className="w-5 h-5" /> Cost Calculation Logic
                        </h3>
                        <div className="text-sm text-blue-800 space-y-1 font-mono">
                            <p>Green Cost   = Actual Green Consumed × ₹3.12</p>
                            <p>Deficit Cost = (Demand - Green Gen) × Hourly MCP</p>
                            <p>Savings      = (Demand × MCP) - (Green Cost + Deficit Cost)</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-blue-600 uppercase font-bold mb-1">Current Scenario Net Savings</p>
                        <p className="text-3xl font-bold text-blue-700">₹{summary.netSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        <p className="text-xs text-blue-500">Daily Projection</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Left Column: Charts */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* 4️⃣ Hourly Cost Breakdown Chart */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold text-slate-800">Hourly Power Procurement Cost Breakdown</h3>
                                <div className="flex items-center gap-4 text-xs font-medium">
                                    <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-500"></span> PPA Cost</div>
                                    <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-500"></span> IEX Cost</div>
                                    <div className="flex items-center gap-1.5"><span className="w-3 h-1 bg-slate-400 border-t border-dashed"></span> Reference (100% Market)</div>
                                </div>
                            </div>
                            <div className="h-[320px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={costData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={2} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} unit="₹" />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="greenCost" stackId="a" fill="#22c55e" name="Green Energy Cost" />
                                        <Bar dataKey="deficitCost" stackId="a" fill="#f43f5e" name="Deficit Market Cost" />
                                        <Line type="monotone" dataKey="refCost" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Reference Market Cost" />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* 5️⃣ RE100 Granular Compliance View */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-slate-800">Granular RE100 (24×7) Green Coverage</h3>
                            </div>
                            <div className="grid grid-cols-6 sm:grid-cols-12 gap-1">
                                {costData.map((d, i) => (
                                    <div key={i} className="flex flex-col items-center group relative">
                                        <div
                                            className={`w-full h-12 rounded-sm transition-all ${d.isFullyGreen ? 'bg-green-500' : d.greenCoveragePct > 50 ? 'bg-amber-400' : 'bg-rose-500'
                                                }`}
                                        />
                                        <span className="text-[10px] text-slate-500 mt-1">{d.hour.split(':')[0]}</span>
                                        {/* Tooltip */}
                                        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 pointer-events-none z-10 w-max">
                                            <p className="font-bold">{d.hour}</p>
                                            <p>Coverage: {Math.round(d.greenCoveragePct)}%</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex justify-center gap-6 text-xs font-medium text-slate-600">
                                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-sm"></div> 100% Green</div>
                                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-amber-400 rounded-sm"></div> Partial Green</div>
                                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-rose-500 rounded-sm"></div> Deficit (Market)</div>
                            </div>
                        </div>

                    </div>

                    {/* Right Column: Simulator & KPIs */}
                    <div className="space-y-6">

                        {/* 6️⃣ Battery Storage Impact Simulator */}
                        <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100 shadow-sm">
                            <div className="flex items-center gap-2 mb-4 text-indigo-900">
                                <Battery className="w-6 h-6" />
                                <h3 className="font-bold text-lg">Battery Storage Impact</h3>
                            </div>

                            <div className="mb-6">
                                <div className="flex justify-between text-sm font-bold text-slate-700 mb-2">
                                    <span>Storage Capacity</span>
                                    <span>{batteryCapacity} kWh</span>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="50000"
                                    step="1000"
                                    value={batteryCapacity}
                                    onChange={(e) => setBatteryCapacity(Number(e.target.value))}
                                    className="w-full h-2 bg-indigo-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                />
                                <div className="flex justify-between text-xs text-slate-500 mt-1">
                                    <span>0 kWh</span>
                                    <span>50,000 kWh</span>
                                </div>
                            </div>

                            <div className="space-y-3 bg-white p-4 rounded-lg border border-indigo-100">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-slate-600">Avoided Peak Cost</span>
                                    <span className="font-bold text-indigo-600">₹{(batteryCapacity * 6).toLocaleString()}</span> {/* Rough heuristic */}
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-slate-600">RE100 Boost</span>
                                    <span className="font-bold text-green-600">+{totalCons > 0 ? ((batteryCapacity / totalCons) * 100).toFixed(1) : 0}%</span>
                                </div>
                            </div>
                        </div>

                        {/* 7️⃣ Cost Savings Summary (Decision Tiles) */}
                        <div className="grid grid-cols-1 gap-4">
                            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                <p className="text-xs text-slate-500 uppercase font-bold">Total Daily Cost</p>
                                <p className="text-2xl font-bold text-slate-800">₹{summary.totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                <p className="text-xs text-emerald-600 mt-1 font-medium">vs ₹{summary.totalReferenceCost.toLocaleString(undefined, { maximumFractionDigits: 0 })} (Reference)</p>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                <p className="text-xs text-slate-500 uppercase font-bold">Net Daily Savings</p>
                                <p className="text-2xl font-bold text-emerald-600">₹{summary.netSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                <p className="text-xs text-slate-400 mt-1">Annual proj: ₹{(summary.netSavings * 365 / 100000).toFixed(1)} Lakhs</p>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                <p className="text-xs text-slate-500 uppercase font-bold">RE100 Compliance</p>
                                <p className="text-2xl font-bold text-blue-600">{summary.re100Compliance.toFixed(1)}%</p>
                                <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden">
                                    <div className="bg-blue-500 h-full rounded-full" style={{ width: `${summary.re100Compliance}%` }}></div>
                                </div>
                            </div>
                        </div>

                        {/* 8️⃣ Executive Insight Panel */}
                        <div className="bg-slate-800 text-white p-5 rounded-xl shadow-md">
                            <h4 className="font-bold text-slate-200 mb-3 flex items-center gap-2">
                                <Info className="w-4 h-4" /> Executive Insights
                            </h4>
                            <ul className="space-y-3 text-sm text-slate-300">
                                <li className="flex gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                    <span>Peak MCP exposure reduced by <strong>{batteryCapacity > 0 ? 'significant margins' : '0%'}</strong> via storage.</span>
                                </li>
                                <li className="flex gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                    <span>Battery enables shifting ₹3.12 power to offset ₹10.20 peak usage.</span>
                                </li>
                                <li className="flex gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                    <span>Granular RE100 achieved for <strong>{costData.filter(d => d.isFullyGreen).length} hours</strong> today.</span>
                                </li>
                            </ul>
                        </div>

                    </div>
                </div>

                {/* 9️⃣ Footer */}
                <footer className="mt-8 border-t border-slate-200 pt-6 text-center">
                    <p className="text-slate-500 text-sm font-medium italic">
                        "This analysis supports granular RE100 compliance by demonstrating hourly green energy matching and quantifying cost optimization through battery-enabled time shifting."
                    </p>
                </footer>

            </div>
        </div>
    );
};

const HourlyBalanceView = ({ onBack, dashboardData }) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [viewFormat, setViewFormat] = useState('chart');

    const totalSurplus = dashboardData.reduce((acc, curr) => curr.balance > 0 ? acc + curr.balance : acc, 0);
    const totalDeficit = dashboardData.reduce((acc, curr) => curr.balance < 0 ? acc + Math.abs(curr.balance) : acc, 0);
    const deficitHours = dashboardData.filter(d => d.balance < 0);

    return (
        <div className="animate-in fade-in duration-300">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <nav className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={onBack}>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="font-semibold text-slate-900">Hourly Energy Balance</span>
                    </nav>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">Hourly Energy Balance</h1>
                                <div className="flex items-center gap-4 text-xs font-medium text-slate-500 mt-1">
                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Oct 24, 2025</span>
                                    <span className="flex items-center gap-1"><Wind className="w-3 h-3" /> Source: Wind Farm A</span>
                                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Region: North-West</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                                <button
                                    onClick={() => setViewFormat('chart')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-2 transition-all ${viewFormat === 'chart' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <BarChart2 className="w-4 h-4" /> Chart
                                </button>
                                <button
                                    onClick={() => setViewFormat('table')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-2 transition-all ${viewFormat === 'table' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <TableIcon className="w-4 h-4" /> Table
                                </button>
                            </div>
                            <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                                <Download className="w-4 h-4" /> Export
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Chart */}
                        {viewFormat === 'chart' && (
                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-bold text-slate-800">Net Energy Balance (Generation − Consumption)</h3>
                                    <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                                            <div className={`w-10 h-5 rounded-full p-1 transition-colors ${showOverlay ? 'bg-blue-600' : 'bg-slate-300'}`} onClick={() => setShowOverlay(!showOverlay)}>
                                                <div className={`bg-white w-3 h-3 rounded-full shadow-sm transition-transform transform ${showOverlay ? 'translate-x-5' : 'translate-x-0'}`} />
                                            </div>
                                            <span>Overlay Gen/Cons</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="h-[400px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={dashboardData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={2} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1} />
                                            <Bar dataKey="balance" name="Balance" barSize={20} radius={[2, 2, 0, 0]}>
                                                {dashboardData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.balance >= 0 ? '#10b981' : '#f43f5e'} />
                                                ))}
                                            </Bar>

                                            {showOverlay && (
                                                <>
                                                    <Line type="monotone" dataKey="generation" stroke="#0d9488" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Generation" />
                                                    <Line type="monotone" dataKey="consumption" stroke="#4f46e5" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Consumption" />
                                                </>
                                            )}
                                            {showOverlay && <Legend />}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="mt-4 flex justify-center gap-6 text-xs text-slate-500 font-medium">
                                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-emerald-500 rounded-sm"></div> Surplus ({'>'} 0 kWh)</div>
                                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-rose-500 rounded-sm"></div> Deficit (&lt; 0 kWh)</div>
                                    {showOverlay && (
                                        <>
                                            <div className="flex items-center gap-2"><div className="w-3 h-1 bg-teal-600 border-t border-dashed"></div> Generation</div>
                                            <div className="flex items-center gap-2"><div className="w-3 h-1 bg-indigo-600 border-t border-dashed"></div> Consumption</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {viewFormat === 'table' && (
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-6 py-3 font-medium">Hour</th>
                                                <th className="px-6 py-3 font-medium text-right">Generation (kWh)</th>
                                                <th className="px-6 py-3 font-medium text-right">Consumption (kWh)</th>
                                                <th className="px-6 py-3 font-medium text-right">Net Balance (kWh)</th>
                                                <th className="px-6 py-3 font-medium text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {dashboardData.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-3 font-medium text-slate-700">{row.hour}</td>
                                                    <td className="px-6 py-3 text-right text-slate-600">{row.generation.toLocaleString()}</td>
                                                    <td className="px-6 py-3 text-right text-slate-600">{row.consumption.toLocaleString()}</td>
                                                    <td className={`px-6 py-3 text-right font-bold ${row.balance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                        {row.balance > 0 ? '+' : ''}{row.balance.toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-3 text-center">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.balance >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                                            }`}>
                                                            {row.balance >= 0 ? 'Surplus' : 'Deficit'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Alerts - Now Dynamic */}
                        {deficitHours.length > 0 ? (
                            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                <div className="p-3 bg-white rounded-full shadow-sm">
                                    <AlertTriangle className="w-8 h-8 text-rose-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-lg font-bold text-rose-900 mb-1">Critical Deficit Hours Detected</h4>
                                    <div className="flex flex-wrap gap-2 my-2">
                                        {deficitHours.map((h, i) => (
                                            <span key={i} className="px-2 py-1 bg-rose-200 text-rose-800 rounded text-xs font-bold">
                                                {h.hour}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-sm text-rose-800">Deficits exceed grid stability thresholds. Requires immediate dispatch from stored reserves or grid import.</p>
                                </div>
                                <button className="px-4 py-2 bg-rose-600 text-white text-sm font-bold rounded-lg hover:bg-rose-700 shadow-sm shrink-0">
                                    View Dispatch Plan
                                </button>
                            </div>
                        ) : (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                <div className="p-3 bg-white rounded-full shadow-sm">
                                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-lg font-bold text-emerald-900 mb-1">No Critical Deficits</h4>
                                    <p className="text-sm text-emerald-800">Generation meets or exceeds consumption for all hours. System is balanced.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Panel */}
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-6">
                            <h3 className="font-bold text-slate-800 border-b border-slate-100 pb-3">Daily Totals</h3>
                            <div>
                                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Total Surplus Energy</p>
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                                    <span className="text-2xl font-bold text-slate-800">{totalSurplus.toLocaleString()}</span>
                                    <span className="text-sm text-slate-500">kWh</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Total Deficit Energy</p>
                                <div className="flex items-center gap-2">
                                    <TrendingDown className="w-5 h-5 text-rose-500" />
                                    <span className="text-2xl font-bold text-slate-800">{totalDeficit.toLocaleString()}</span>
                                    <span className="text-sm text-slate-500">kWh</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100 shadow-sm">
                            <h3 className="flex items-center gap-2 font-bold text-indigo-900 mb-4">
                                <Battery className="w-5 h-5" /> Banking Logic
                            </h3>
                            <div className="relative pl-4 border-l-2 border-indigo-200 space-y-6">
                                <div className="relative">
                                    <span className="absolute -left-[21px] top-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-indigo-50"></span>
                                    <p className="text-xs font-bold text-indigo-400 uppercase mb-1">00:00 – 06:00</p>
                                    <p className="text-sm font-medium text-slate-700">Excess generation banked to storage.</p>
                                </div>
                                <div className="relative">
                                    <span className="absolute -left-[21px] top-1 w-3 h-3 bg-slate-400 rounded-full border-2 border-indigo-50"></span>
                                    <p className="text-xs font-bold text-indigo-400 uppercase mb-1">07:00 – 16:00</p>
                                    <p className="text-sm font-medium text-slate-700">Direct consumption & minor top-ups.</p>
                                </div>
                                <div className="relative">
                                    <span className="absolute -left-[21px] top-1 w-3 h-3 bg-rose-500 rounded-full border-2 border-indigo-50"></span>
                                    <p className="text-xs font-bold text-indigo-400 uppercase mb-1">17:00 – 21:00</p>
                                    <p className="text-sm font-medium text-slate-700">Storage discharge offsets grid demand.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="mt-8 pt-6 border-t border-slate-200 text-center">
                    <p className="text-slate-500 text-sm">
                        <span className="font-bold text-slate-700">Operational Insight:</span> Hourly deficits during evening peaks require energy banking or storage despite substantial daily surplus generation.
                    </p>
                </footer>
            </div>
        </div>
    );
};

const StackedAnalysisView = ({ onBack, dashboardData, consumerKeys, consumerColors, generatorKeys, generatorColors }) => {
    const [viewMode, setViewMode] = useState('absolute');
    const [showTable, setShowTable] = useState(false);
    const [consumerFilter, setConsumerFilter] = useState('');
    const [showGenTable, setShowGenTable] = useState(false);
    const [generatorFilter, setGeneratorFilter] = useState('');

    const deficitHours = dashboardData.filter(d => d.balance < 0);

    const filteredConsumerKeys = consumerKeys.filter(key =>
        key.toLowerCase().includes(consumerFilter.toLowerCase())
    );

    const filteredGeneratorKeys = generatorKeys.filter(key =>
        key.toLowerCase().includes(generatorFilter.toLowerCase())
    );

    return (
        <div className="animate-in fade-in duration-300 pb-12">
            <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <nav className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={onBack}>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={onBack}>Generation vs Consumption</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="font-semibold text-slate-900">Stacked Analysis</span>
                    </nav>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">Stacked Energy Analysis</h1>
                                <p className="text-xs text-slate-500 font-medium mt-1">Hourly – Meter Level Disaggregation</p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 flex items-center gap-2">
                                <Calendar className="w-4 h-4" /> Oct 24, 2025
                            </div>
                            <div className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> North-West Feeder
                            </div>
                            <div className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 font-bold">
                                Hourly
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

                <div className="flex justify-end">
                    <div className="bg-white p-1 rounded-lg border border-slate-200 shadow-sm flex items-center">
                        <button
                            onClick={() => setViewMode('absolute')}
                            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'absolute' ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Absolute kWh
                        </button>
                        <button
                            onClick={() => setViewMode('percentage')}
                            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'percentage' ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            % Contribution
                        </button>
                    </div>
                </div>

                <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-blue-600" />
                            System-Level Baseline (Gen vs Cons)
                        </h2>
                        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded">Reference Layer</span>
                    </div>
                    <div className="h-[250px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={dashboardData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} syncId="energyStack">
                                <defs>
                                    <linearGradient id="gradGen" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.1} />
                                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={false} height={0} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="generation" stroke="#14b8a6" strokeWidth={2} fill="url(#gradGen)" name="Total Generation" />
                                <Area type="monotone" dataKey="consumption" stroke="#6366f1" strokeWidth={2} fill="transparent" name="Total Consumption" />
                                <Legend verticalAlign="top" height={36} iconType="circle" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Users className="w-5 h-5 text-indigo-600" />
                            Consumers Load Profile – Stacked View
                        </h2>
                        <div className="flex items-center gap-3">
                            {showTable && (
                                <div className="relative">
                                    <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    <input
                                        type="text"
                                        placeholder="Filter Consumer ID..."
                                        value={consumerFilter}
                                        onChange={(e) => setConsumerFilter(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
                                    />
                                </div>
                            )}
                            <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                                <button
                                    onClick={() => setShowTable(false)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${!showTable ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <BarChart2 className="w-3.5 h-3.5" /> Chart
                                </button>
                                <button
                                    onClick={() => setShowTable(true)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${showTable ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <TableIcon className="w-3.5 h-3.5" /> Table
                                </button>
                            </div>
                        </div>
                    </div>

                    {showTable ? (
                        <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[400px]">
                            <table className="w-full text-xs text-left whitespace-nowrap">
                                <thead className="bg-slate-50 sticky top-0 z-30 shadow-sm">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 bg-slate-50 sticky left-0 z-20">Consumer ID</th>
                                        {dashboardData.map((row, idx) => (
                                            <th key={idx} className="px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 text-center">
                                                {row.hour}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredConsumerKeys.map((key) => (
                                        <tr key={key} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-4 py-2 font-medium text-slate-700 sticky left-0 bg-white border-r border-slate-100 z-10">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: consumerColors[key] }} />
                                                    {key}
                                                </div>
                                            </td>
                                            {dashboardData.map((row, idx) => (
                                                <td key={idx} className="px-4 py-2 text-slate-500 font-mono text-right">
                                                    {row[key]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredConsumerKeys.length === 0 && (
                                <div className="p-8 text-center text-slate-500">
                                    No consumers found matching "{consumerFilter}"
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="h-[400px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={dashboardData}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                    syncId="energyStack"
                                    stackOffset={viewMode === "percentage" ? "expand" : "none"}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="hour"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={false}
                                        height={0}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: "#64748b" }}
                                        tickFormatter={
                                            viewMode === "percentage"
                                                ? (val) => `${(val * 100).toFixed(0)}%`
                                                : (val) => val
                                        }
                                    />
                                    <Tooltip content={<CustomTooltip />} />

                                    {consumerKeys.map((key) => (
                                        <Bar
                                            key={key}
                                            dataKey={key}
                                            stackId="1"
                                            fill={consumerColors[key]}
                                            name={`Meter ${key}`}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </section>


                <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Factory className="w-5 h-5 text-teal-600" />
                            Plants Generation Profile – Stacked View
                        </h2>
                        <div className="flex items-center gap-3">
                            {showGenTable && (
                                <div className="relative">
                                    <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    <input
                                        type="text"
                                        placeholder="Filter Generator ID..."
                                        value={generatorFilter}
                                        onChange={(e) => setGeneratorFilter(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 w-48"
                                    />
                                </div>
                            )}
                            <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                                <button
                                    onClick={() => setShowGenTable(false)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${!showGenTable ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <BarChart2 className="w-3.5 h-3.5" /> Chart
                                </button>
                                <button
                                    onClick={() => setShowGenTable(true)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-2 transition-all ${showGenTable ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <TableIcon className="w-3.5 h-3.5" /> Table
                                </button>
                            </div>
                        </div>
                    </div>
                    {showGenTable ? (
                        <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[400px]">
                            <table className="w-full text-xs text-left whitespace-nowrap">
                                <thead className="bg-slate-50 sticky top-0 z-30 shadow-sm">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 bg-slate-50 sticky left-0 z-20">Generator ID</th>
                                        {dashboardData.map((row, idx) => (
                                            <th key={idx} className="px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 text-center">
                                                {row.hour}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredGeneratorKeys.map((key) => (
                                        <tr key={key} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-4 py-2 font-medium text-slate-700 sticky left-0 bg-white border-r border-slate-100 z-10">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: generatorColors[key] }} />
                                                    {key}
                                                </div>
                                            </td>
                                            {dashboardData.map((row, idx) => (
                                                <td key={idx} className="px-4 py-2 text-slate-500 font-mono text-right">
                                                    {row[key]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredGeneratorKeys.length === 0 && (
                                <div className="p-8 text-center text-slate-500">
                                    No generators found matching "{generatorFilter}"
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="h-[400px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={dashboardData}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                    syncId="energyStack"
                                    stackOffset={viewMode === 'percentage' ? 'expand' : 'none'}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={2} />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: '#64748b' }}
                                        tickFormatter={viewMode === 'percentage' ? (val) => `${(val * 100).toFixed(0)}%` : (val) => val}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    {generatorKeys.map((key) => (
                                        <Bar
                                            key={key}
                                            dataKey={key}
                                            stackId="1"
                                            fill={generatorColors[key]}
                                            name={`${key}`}
                                        />
                                    ))}
                                    <Legend verticalAlign="top" height={60} iconType="circle" wrapperStyle={{ fontSize: '10px' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </section>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                        <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                            <ArrowRight className="w-4 h-4" /> Demand Insight
                        </h4>
                        <p className="text-sm text-blue-800">
                            Consumption is driven by two key meters (X2394288 & X2394289) which peak significantly during the evening (18:00 - 22:00), correlating perfectly with the critical deficit window.
                        </p>
                    </div>
                    <div className="bg-teal-50 border border-teal-100 p-4 rounded-xl">
                        <h4 className="font-bold text-teal-900 mb-2 flex items-center gap-2">
                            <ArrowRight className="w-4 h-4" /> Supply Insight
                        </h4>
                        <p className="text-sm text-teal-800">
                            Wind generation remains substantial across the day, though it experiences a noticeable lull during midday (08:00-11:00) where consumption remains high.
                        </p>
                    </div>
                </div>

                {/* Alerts - Also Dynamic Here */}
                <div className="mt-6">
                    {deficitHours.length > 0 ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            <div className="p-3 bg-white rounded-full shadow-sm">
                                <AlertTriangle className="w-8 h-8 text-rose-600" />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-lg font-bold text-rose-900 mb-1">Critical Deficit Hours Detected</h4>
                                <div className="flex flex-wrap gap-2 my-2">
                                    {deficitHours.map((h, i) => (
                                        <span key={i} className="px-2 py-1 bg-rose-200 text-rose-800 rounded text-xs font-bold">
                                            {h.hour}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-sm text-rose-800">Deficits exceed grid stability thresholds. Requires immediate dispatch from stored reserves or grid import.</p>
                            </div>
                            <button className="px-4 py-2 bg-rose-600 text-white text-sm font-bold rounded-lg hover:bg-rose-700 shadow-sm shrink-0">
                                View Dispatch Plan
                            </button>
                        </div>
                    ) : (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            <div className="p-3 bg-white rounded-full shadow-sm">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-lg font-bold text-emerald-900 mb-1">No Critical Deficits</h4>
                                <p className="text-sm text-emerald-800">Generation meets or exceeds consumption for all hours. System is balanced.</p>
                            </div>
                        </div>
                    )}
                </div>

                <footer className="pt-6 border-t border-slate-200 text-center">
                    <p className="text-slate-500 text-sm font-medium">
                        <span className="font-bold text-slate-700">Executive Insight:</span> Stacked analysis identifies Meter X2394288 and X2394289 as the primary drivers of evening peak demand, necessitating targeted Demand Response or storage allocation.
                    </p>
                </footer>
            </div>
        </div>
    );
}

// --- Main Application Component ---
export default function ReportPage2() {
    const [currentView, setCurrentView] = useState('summary');
    const [bessCapacity, setBessCapacity] = useState(10000); // User selected BESS capacity in kWh
    const [loading, setLoading] = useState(true);

    const { consumerLoadProfile, plantGenerationProfile } = useEnergyAnalysisStore()

    // FETCH DATA ON MOUNT
    // useEffect(() => {
    //     fetchEnergyData().then(data => {
    //         // console.log(data);
    //         setRawData(data);
    //         setLoading(false);
    //     });
    // }, []);

    const rawData = useMemo(() => {
        return { consumerRawData: consumerLoadProfile || [], generatorRawData: plantGenerationProfile || [] }
    }, [consumerLoadProfile, plantGenerationProfile])

    // COMPUTE DERIVED DATA (Only when rawData is available)
    const processedData = useMemo(() => {
        if (!rawData) {
            setLoading(false);
            return null
        };

        const { consumerRawData, generatorRawData } = rawData;
        const consumerKeys = Object.keys(consumerRawData);
        const generatorKeys = Object.keys(generatorRawData);

        const consumerColors = generateColorPalette(consumerKeys, 30); // Orange hue
        const generatorColors = generateColorPalette(generatorKeys, 145); // Teal hue

        // Calculate Total Consumption Array
        const newTotalConsumption = Array(24).fill(0);
        Object.values(consumerRawData).forEach(category => {
            category.forEach((value, hour) => {
                newTotalConsumption[hour] += value;
            });
        });

        const data = [];
        let totalGen = 0;
        let totalCons = 0;
        let maxDeficit = 0;
        let maxDeficitHour = "";
        let maxSurplus = 0;
        let maxSurplusHour = "";

        for (let i = 0; i < 24; i++) {
            const cons = newTotalConsumption[i];

            // Consumer disaggregation
            const consumerValues = {};
            consumerKeys.forEach(key => consumerValues[key] = consumerRawData[key][i]);

            // Generator disaggregation and total
            let hourlyGenTotal = 0;
            const generatorValues = {};
            generatorKeys.forEach(key => {
                const val = generatorRawData[key][i];
                generatorValues[key] = val;
                hourlyGenTotal += val;
            });

            const gen = parseFloat(hourlyGenTotal.toFixed(2));
            const balance = parseFloat((gen - cons).toFixed(2));
            const mcp = hourlyMCP[i];

            // Track max deficit/surplus
            if (balance < maxDeficit) {
                maxDeficit = balance;
                maxDeficitHour = `${i}:00`;
            }
            if (balance > maxSurplus) {
                maxSurplus = balance;
                maxSurplusHour = `${i}:00`;
            }

            totalGen += gen;
            totalCons += cons;

            data.push({
                hour: `${i}:00`,
                generation: gen,
                consumption: cons,
                balance: balance,
                isSurplus: balance >= 0,
                mcp: mcp,
                ...consumerValues,
                ...generatorValues
            });
        }

        const insights = analyzeEnergyData(data);
        setLoading(false);
        return {
            dashboardData: data,
            totalGen: Math.round(totalGen),
            totalCons: Math.round(totalCons),
            maxDeficitHour,
            maxDeficit: parseFloat(maxDeficit.toFixed(2)),
            maxSurplus: parseFloat(maxSurplus.toFixed(2)),
            maxSurplusHour,
            insights,
            consumerKeys,
            consumerColors,
            generatorKeys,
            generatorColors
        };

    }, [rawData]);

    if (loading || !processedData) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-slate-500 gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
                <p className="font-medium animate-pulse">Loading Energy Data...</p>
            </div>
        );
    }

    const {
        dashboardData, totalGen, totalCons, maxDeficitHour, maxDeficit,
        maxSurplus, maxSurplusHour, insights, consumerKeys, consumerColors,
        generatorKeys, generatorColors
    } = processedData;

    // Summary View Aggregate Calculations
    const netBalance = totalGen - totalCons;
    const netBalancePercentage = totalCons > 0 ? ((netBalance / totalCons) * 100).toFixed(1) : 0;
    const netCoverage = totalCons > 0 ? ((totalGen / totalCons) * 100).toFixed(1) : 0;

    // Green Energy Consumed
    const greenEnergyConsumed = dashboardData.reduce((acc, curr) => acc + Math.min(curr.generation, curr.consumption), 0);
    const greenEnergyPercentage = totalCons > 0 ? ((greenEnergyConsumed / totalCons) * 100).toFixed(1) : 0;
    const gridEnergy = totalCons - greenEnergyConsumed;
    const gridEnergyPercentage = totalCons > 0 ? ((gridEnergy / totalCons) * 100).toFixed(1) : 0;

    // BESS Insight Calculation
    const totalDeficit = dashboardData.reduce((acc, curr) => curr.balance < 0 ? acc + Math.abs(curr.balance) : acc, 0);
    // FIX: Define totalSurplus for use in summary tiles
    const totalSurplus = dashboardData.reduce((acc, curr) => curr.balance > 0 ? acc + curr.balance : acc, 0);

    let currentStorage = 0;
    let coveredDeficit = 0;

    dashboardData.forEach(d => {
        const surplus = Math.max(0, d.generation - d.consumption);
        const deficit = Math.max(0, d.consumption - d.generation);

        if (surplus > 0) {
            const space = bessCapacity - currentStorage;
            const charge = Math.min(surplus, space);
            currentStorage += charge;
        } else if (deficit > 0) {
            const discharge = Math.min(deficit, currentStorage);
            currentStorage -= discharge;
            coveredDeficit += discharge;
        }
    });

    const bessCoveragePercent = totalDeficit > 0 ? Math.round((coveredDeficit / totalDeficit) * 100) : 0;

    // --- ROUTING ---
    if (currentView === 'hourly') {
        return <HourlyBalanceView onBack={() => setCurrentView('summary')} dashboardData={dashboardData} />;
    }

    if (currentView === 'stacked-analysis') {
        return (
            <StackedAnalysisView
                onBack={() => setCurrentView('summary')}
                dashboardData={dashboardData}
                consumerKeys={consumerKeys}
                consumerColors={consumerColors}
                generatorKeys={generatorKeys}
                generatorColors={generatorColors}
            />
        );
    }

    if (currentView === 'cost-analysis') {
        return <CostAnalysisView onBack={() => setCurrentView('summary')} dashboardData={dashboardData} totalCons={totalCons} />;
    }

    // --- Render Summary Dashboard ---
    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-12 animate-in fade-in duration-300">

            {/* 2️⃣ Header Section */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-600 p-2 rounded-lg">
                            <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-slate-900 leading-tight">Energy Summary Dashboard</h1>
                            <p className="text-xs text-slate-500 font-medium">Hourly – Single Day | Wind-Dominated Generation</p>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-md text-slate-600 border border-slate-200">
                            <Calendar className="w-4 h-4" />
                            <span>Oct 24, 2025</span>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-md text-slate-600 border border-slate-200">
                            <Wind className="w-4 h-4" />
                            <span>Wind Farm A</span>
                        </div>
                        <button
                            onClick={() => setCurrentView('hourly')}
                            className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-md text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors font-medium"
                        >
                            <Activity className="w-4 h-4" />
                            <span>Hourly View</span>
                            <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

                {/* 3️⃣ KPI Tiles Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard
                        title="Total Generation"
                        value={`${totalGen.toLocaleString()} kWh`}
                        subtext="Wind Source"
                        icon={Wind}
                        colorClass="bg-teal-500 text-teal-600"
                        trend="neutral"
                    />
                    <KPICard
                        title="Total Consumption"
                        value={`${totalCons.toLocaleString()} kWh`}
                        subtext="Utility Demand"
                        icon={Activity}
                        colorClass="bg-indigo-500 text-indigo-600"
                        trend="neutral"
                    />
                    <KPICard
                        title="Net Balance"
                        value={`${netBalance > 0 ? '+' : ''}${netBalance.toLocaleString()}`}
                        subtext={`${netBalancePercentage}% of Demand`}
                        icon={netBalance >= 0 ? TrendingUp : TrendingDown}
                        colorClass={netBalance >= 0 ? "bg-emerald-500 text-emerald-600" : "bg-rose-500 text-rose-600"}
                        trend={netBalance >= 0 ? "positive" : "negative"}
                    />
                    <KPICard
                        title="Net Coverage"
                        value={`${netCoverage}%`}
                        subtext="Generation / Consumption"
                        icon={Target}
                        colorClass="bg-blue-500 text-blue-600"
                        trend="positive"
                    />
                    <KPICard
                        title="Green Energy Consumed"
                        value={`${greenEnergyConsumed.toLocaleString()}`}
                        subtext={`${greenEnergyPercentage}% of Total Cons`}
                        icon={Leaf}
                        colorClass="bg-green-500 text-green-600"
                        trend="positive"
                    />
                    <KPICard
                        title="Grid Energy"
                        value={`${Math.round(gridEnergy).toLocaleString()} kWh`}
                        subtext={`${gridEnergyPercentage}% of Total Cons`}
                        icon={Zap}
                        colorClass="bg-amber-500 text-amber-600"
                        trend="neutral"
                    />
                    <KPICard
                        title="Peak Deficit"
                        value={`${maxDeficit.toLocaleString()}`}
                        subtext={`at ${maxDeficitHour}`}
                        icon={Minimize2}
                        colorClass="bg-rose-500 text-rose-600"
                        trend="negative"
                    />
                    <KPICard
                        title="Peak Surplus"
                        value={`+${maxSurplus.toLocaleString()}`}
                        subtext={`at ${maxSurplusHour}`}
                        icon={Maximize2}
                        colorClass="bg-teal-500 text-teal-600"
                        trend="positive"
                    />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Left Column (Charts) - Spans 2 cols */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* 4️⃣ Generation vs Consumption Chart */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-bold text-slate-800">Generation vs Consumption</h2>
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setCurrentView('stacked-analysis')}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                                    >
                                        <Layers className="w-3.5 h-3.5" />
                                        View Stacked Analysis
                                    </button>
                                    <button
                                        onClick={() => setCurrentView('cost-analysis')}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                                    >
                                        <IndianRupee className="w-3.5 h-3.5" />
                                        View Cost Analysis
                                    </button>
                                    <div className="flex gap-4 text-xs font-medium">
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-3 h-3 rounded-full bg-teal-500"></span> Gen
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-3 h-3 rounded-full bg-indigo-500"></span> Cons
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="h-[320px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={dashboardData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorGen" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorCons" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={3} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area type="monotone" dataKey="generation" stroke="#14b8a6" strokeWidth={3} fillOpacity={1} fill="url(#colorGen)" name="Generation" />
                                        <Area type="monotone" dataKey="consumption" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorCons)" name="Consumption" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <ActualGreenConsumption data={dashboardData} />

                        {/* 5️⃣ Hourly Energy Balance Indicator */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative group cursor-pointer hover:border-blue-400 transition-colors" onClick={() => setCurrentView('hourly')}>
                            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-blue-50 text-blue-600 text-xs font-bold px-2 py-1 rounded-md flex items-center gap-1">
                                Click for Detail View <ArrowRight className="w-3 h-3" />
                            </div>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-bold text-slate-800">Hourly Energy Balance</h2>
                                <div className="flex gap-4 text-xs font-medium">
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-3 h-3 rounded bg-emerald-500"></span> Surplus
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-3 h-3 rounded bg-rose-500"></span> Deficit
                                    </div>
                                </div>
                            </div>

                            <div className="h-[200px] w-full pointer-events-none">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dashboardData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} interval={2} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                                        <ReferenceLine y={0} stroke="#94a3b8" />
                                        <Bar dataKey="balance" radius={[2, 2, 0, 0]}>
                                            {dashboardData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.balance >= 0 ? '#10b981' : '#f43f5e'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                    </div>

                    {/* Right Column (Insights & Details) */}
                    <div className="space-y-6">

                        {/* 6️⃣ Surplus vs Deficit Summary */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Daily Balance Sheet</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                                    <div>
                                        <p className="text-xs text-emerald-600 font-bold uppercase">Total Surplus</p>
                                        <p className="text-xl font-bold text-emerald-700">{totalSurplus.toLocaleString()} kWh</p>
                                    </div>
                                    <TrendingUp className="text-emerald-400 w-8 h-8" />
                                </div>

                                <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-100">
                                    <div>
                                        <p className="text-xs text-rose-600 font-bold uppercase">Total Deficit</p>
                                        <p className="text-xl font-bold text-rose-700">{totalDeficit.toLocaleString()} kWh</p>
                                    </div>
                                    <TrendingDown className="text-rose-400 w-8 h-8" />
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Net Coverage:</span>
                                        <span className="font-bold text-slate-700">{Math.round(totalCons > 0 ? (totalGen / totalCons) * 100 : 0)}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 8️⃣ Peak Mismatch Alert Panel - Now Dynamic */}
                        <div className="bg-amber-50 p-6 rounded-xl border border-amber-200 shadow-sm relative overflow-hidden">
                            <div className="absolute right-0 top-0 p-4 opacity-10">
                                <AlertTriangle className="w-24 h-24 text-amber-600" />
                            </div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-2 text-amber-800 font-bold">
                                    <AlertTriangle className="w-5 h-5" />
                                    <h3>Peak Demand Mismatch</h3>
                                </div>
                                {/* Dynamic Mismatch Data */}
                                <p className="text-2xl font-bold text-slate-800 mb-1">{insights.mismatchTime}</p>
                                <p className="text-sm text-amber-800 mb-3 font-medium">{insights.mismatchText}</p>
                                <p className="text-xs text-slate-600 leading-relaxed">
                                    {insights.mismatchNote}
                                </p>
                            </div>
                        </div>

                        {/* 7️⃣ Wind Generation Profile Card */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-2">
                                <Wind className="w-5 h-5 text-teal-500" />
                                <h3 className="font-bold text-slate-700">Generation Profile</h3>
                            </div>
                            <ul className="space-y-3 text-sm text-slate-600">
                                <li className="flex items-start gap-3">
                                    <Clock className="w-4 h-4 mt-0.5 text-teal-500 shrink-0" />
                                    <span><strong>Morning (00-08):</strong> {insights.genProfile.morning}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <TrendingDown className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                                    <span><strong>Midday:</strong> {insights.genProfile.midday}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-500 shrink-0" />
                                    <span><strong>Evening:</strong> {insights.genProfile.evening}</span>
                                </li>
                            </ul>
                        </div>

                        {/* 9️⃣ Storage & Banking Readiness - Now Interactive */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Battery className="w-5 h-5 text-indigo-500" />
                                    <h3 className="font-bold text-slate-700">BESS Strategy</h3>
                                </div>
                                <span className="text-xs font-bold bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md">Interactive</span>
                            </div>

                            {/* Interactive Slider */}
                            <div className="mb-6 px-1">
                                <div className="flex justify-between text-xs font-semibold text-slate-500 mb-2">
                                    <span>Capacity:</span>
                                    <span className="text-indigo-600 font-bold">{bessCapacity.toLocaleString()} kWh</span>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="20000"
                                    step="500"
                                    value={bessCapacity}
                                    onChange={(e) => setBessCapacity(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600 hover:accent-indigo-500"
                                />
                                <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-medium">
                                    <span>0 kWh</span>
                                    <span>20,000 kWh</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center text-xs">
                                <div className="text-center">
                                    <span className="block font-bold text-slate-800">{insights.chargeWindow}</span>
                                    <span className="text-emerald-600">Aggressive Capture</span>
                                </div>
                                <ArrowRight className="w-4 h-4 text-slate-300" />
                                <div className="text-center">
                                    <span className="block font-bold text-slate-800">{insights.dischargeWindow}</span>
                                    <span className="text-indigo-600">Full Discharge</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* 🔟 Footer Insight - Now Dynamic */}
                <footer className="mt-8 border-t border-slate-200 pt-6 text-center transition-all">
                    <p className="text-slate-500 text-sm font-medium">
                        <span className="font-bold text-slate-700">Executive Insight:</span>
                        {bessCapacity > 0 ? (
                            <> A <strong>{bessCapacity.toLocaleString()} kWh BESS</strong> allows shifting morning wind surplus to cover <strong>{bessCoveragePercent}%</strong> of the critical evening peak deficit.</>
                        ) : (
                            <> Wind-dominated generation creates substantial night-time surplus, but BESS (Battery Energy Storage) integration is essential to cover critical evening peak deficits.</>
                        )}
                    </p>
                </footer>

            </main>
        </div>
    );
}